import { CheckIcon } from '@web/components/atoms/Icons'
import { Box, HStack } from '@web/components/atoms/Layout'
import { Image } from '@web/components/atoms/Image'
import { AnimatePresence, motion } from '@web/components/atoms/Transitions'

type ProgressBarProps = {
  progressPercentage: number
  progressColor: string
  goals: number[]
  totalAmount: number
  barColor: string
  pastGoals: number[]
  bonusGoals?: Array<{ customTickColor?: string }>
  isAnimated?: boolean
  footerSlot?: React.ReactNode
  progressBarIcon?: string
}

const TrophyIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5q0 .807-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33 33 0 0 1 2.5.5m.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935m10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935" />
    </svg>
  )
}

const ProgressBar = ({
  progressPercentage,
  progressColor,
  goals,
  bonusGoals,
  totalAmount,
  isAnimated,
  barColor,
  pastGoals,
  footerSlot,
  progressBarIcon,
}: ProgressBarProps) => (
  <Box w="100%" mt={progressBarIcon ? '52px' : '0'}>
    <Box w="100%" position="relative">
      {progressBarIcon && (
        <Box
          position="relative"
          h="0"
          {...(isAnimated ? { transition: 'width 0.5s linear' } : {})}
          style={{
            width: `${Math.min(progressPercentage, 100)}%`,
          }}
        >
          <Image src={progressBarIcon} minW="52px" w="52px" h="52px" position="absolute" right="-26px" top="-60px" />
        </Box>
      )}
      <Box w="100%" bgColor={barColor} overflowX="hidden" h="24px" mb="8px" borderRadius="16px">
        <Box
          style={{
            width: `${progressPercentage}%`,
          }}
          bgColor={progressColor}
          h="24px"
          {...(isAnimated ? { transition: 'width 0.5s linear' } : {})}
        />
      </Box>
      <AnimatePresence>
        {pastGoals.map((goal, index) => {
          const isFinalGoal = index === goals.length - 1
          return (
            <motion.div
              key={`${goal}-${index}`}
              initial={{ scale: isAnimated ? 0 : 1 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10, stiffness: 100, delay: 0.2 }}
              style={{
                position: 'absolute',
                top: '-3px',
                marginLeft: '-15px',
                left: `${(goal / totalAmount) * 100}%`,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgColor={bonusGoals?.[index].customTickColor ?? 'green'}
                w="30px"
                h="30px"
                borderRadius="50%"
                color="ge.white"
              >
                {isFinalGoal ? <TrophyIcon /> : <CheckIcon />}
              </Box>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </Box>
    <HStack justifyContent="space-between">{footerSlot}</HStack>
  </Box>
)

export default ProgressBar
